import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useAuth } from './contexts/AuthContext'
import { useLanguage } from './contexts/LanguageContext'
import Layout from './components/layout/Layout'
import DemoBanner from './components/ui/DemoBanner'
import DemoWatermark from './components/ui/DemoWatermark'
import ProtectedRoute from './components/auth/ProtectedRoute'
import RoleRoute from './components/auth/RoleRoute'

// Lazy load page components
const Login = lazy(() => import('./pages/auth/Login'))
const Register = lazy(() => import('./pages/auth/Register'))
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'))
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'))

const Dashboard = lazy(() => import('./pages/Dashboard'))
const CourseCatalog = lazy(() => import('./pages/training/CourseCatalog'))
const ModuleViewer = lazy(() => import('./pages/training/ModuleViewer'))
const ScenarioPlayer = lazy(() => import('./pages/training/ScenarioPlayer'))
const ExamSession = lazy(() => import('./pages/exam/ExamSession'))
const ExamResult = lazy(() => import('./pages/exam/ExamResult'))
const CertificateView = lazy(() => import('./pages/certificates/CertificateView'))
const CertificateVerify = lazy(() => import('./pages/certificates/CertificateVerify'))
const MyCertificates = lazy(() => import('./pages/certificates/MyCertificates'))

const OfficerDashboard = lazy(() => import('./pages/officer/OfficerDashboard'))
const OfficerVisits = lazy(() => import('./pages/officer/Visits'))
const OfficerDocuments = lazy(() => import('./pages/officer/Documents'))
const OfficerComplaints = lazy(() => import('./pages/officer/Complaints'))

const HRDashboard = lazy(() => import('./pages/hr/HRDashboard'))
const HRChecklist = lazy(() => import('./pages/hr/Checklist'))
const HRVisits = lazy(() => import('./pages/hr/Visits'))
const HRDocuments = lazy(() => import('./pages/hr/Documents'))
const HRComplaints = lazy(() => import('./pages/hr/Complaints'))
const HRTeam = lazy(() => import('./pages/hr/Team'))
const HRCalendar = lazy(() => import('./pages/hr/Calendar'))

const ComplianceDashboard = lazy(() => import('./pages/compliance/ComplianceDashboard'))
const ComplianceCalendar = lazy(() => import('./pages/compliance/Calendar'))
const ComplianceChecklist = lazy(() => import('./pages/compliance/Checklist'))
const AuditSimulator = lazy(() => import('./pages/compliance/AuditSimulator'))

const WorkflowList = lazy(() => import('./pages/workflows/WorkflowList'))
const WorkflowDetail = lazy(() => import('./pages/workflows/WorkflowDetail'))
const MyWorkflows = lazy(() => import('./pages/workflows/MyWorkflows'))

const ComplaintList = lazy(() => import('./pages/complaints/ComplaintList'))
const ComplaintDetail = lazy(() => import('./pages/complaints/ComplaintDetail'))
const FileComplaint = lazy(() => import('./pages/complaints/FileComplaint'))

const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminAnalytics = lazy(() => import('./pages/admin/Analytics'))
const AdminUsers = lazy(() => import('./pages/admin/Users'))
const AdminCompanies = lazy(() => import('./pages/admin/Companies'))
const AdminModules = lazy(() => import('./pages/admin/Modules'))
const AdminScenarios = lazy(() => import('./pages/admin/Scenarios'))
const AdminQuestions = lazy(() => import('./pages/admin/Questions'))
const AdminExamTemplates = lazy(() => import('./pages/admin/ExamTemplates'))
const AdminCertificationPaths = lazy(() => import('./pages/admin/CertificationPaths'))
const AdminCertificateTemplates = lazy(() => import('./pages/admin/CertificateTemplates'))
const AdminOfficers = lazy(() => import('./pages/admin/Officers'))
const AdminComplaints = lazy(() => import('./pages/admin/Complaints'))
const AdminWorkflows = lazy(() => import('./pages/admin/Workflows'))
const AdminComplianceEvents = lazy(() => import('./pages/admin/ComplianceEvents'))
const AdminChecklistTemplates = lazy(() => import('./pages/admin/ChecklistTemplates'))
const AdminAIProviders = lazy(() => import('./pages/admin/AIProviders'))
const AdminAIPrompts = lazy(() => import('./pages/admin/AIPrompts'))
const AdminSettings = lazy(() => import('./pages/admin/Settings'))
const AdminDemo = lazy(() => import('./pages/admin/Demo'))

const PublicComplaint = lazy(() => import('./pages/public/FileComplaint'))
const PublicVerify = lazy(() => import('./pages/public/VerifyCertificate'))
const LawLibrary = lazy(() => import('./pages/public/LawLibrary'))
const WorkflowDocs = lazy(() => import('./pages/public/WorkflowDocs'))

const Profile = lazy(() => import('./pages/Profile'))
const Settings = lazy(() => import('./pages/Settings'))

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="inline-block w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    </div>
  )
}

function AppRoutes() {
  const { isAuthenticated, user, loading } = useAuth()
  const { language } = useLanguage()

  if (loading) {
    return <LoadingFallback />
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      
      {/* Public pages */}
      <Route path="/verify/:hash" element={<CertificateVerify />} />
      <Route path="/complaint" element={<PublicComplaint />} />
      <Route path="/laws" element={<LawLibrary />} />
      <Route path="/workflows" element={<WorkflowDocs />} />
      <Route path="/workflows/:id" element={<WorkflowDocs />} />
      
      {/* Protected Routes */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        
        {/* Training & Certification */}
        <Route path="/courses" element={<CourseCatalog />} />
        <Route path="/courses/:id" element={<ModuleViewer />} />
        <Route path="/scenarios/:id" element={<ScenarioPlayer />} />
        <Route path="/exam/:sessionId" element={<ExamSession />} />
        <Route path="/exam/:sessionId/result" element={<ExamResult />} />
        
        <Route path="/certificates" element={<MyCertificates />} />
        <Route path="/certificates/:id" element={<CertificateView />} />
        <Route path="/certificates/:id/download" element={<CertificateView />} />
        
        {/* Officer Routes */}
        <Route element={<RoleRoute allowedRoles={['officer', 'admin']} />}>
          <Route path="/officer" element={<OfficerDashboard />} />
          <Route path="/officer/visits" element={<OfficerVisits />} />
          <Route path="/officer/visits/:id" element={<OfficerVisits />} />
          <Route path="/officer/documents" element={<OfficerDocuments />} />
          <Route path="/officer/complaints" element={<OfficerComplaints />} />
        </Route>
        
        {/* HR/Company Routes */}
        <Route element={<RoleRoute allowedRoles={['company_admin', 'hr_rep', 'admin']} />}>
          <Route path="/hr" element={<HRDashboard />} />
          <Route path="/hr/checklist" element={<HRChecklist />} />
          <Route path="/hr/visits" element={<HRVisits />} />
          <Route path="/hr/documents" element={<HRDocuments />} />
          <Route path="/hr/complaints" element={<HRComplaints />} />
          <Route path="/hr/team" element={<HRTeam />} />
          <Route path="/hr/calendar" element={<HRCalendar />} />
        </Route>
        
        {/* Compliance */}
        <Route element={<RoleRoute allowedRoles={['company_admin', 'hr_rep', 'admin', 'officer']} />}>
          <Route path="/compliance" element={<ComplianceDashboard />} />
          <Route path="/compliance/calendar" element={<ComplianceCalendar />} />
          <Route path="/compliance/checklist" element={<ComplianceChecklist />} />
          <Route path="/compliance/audit" element={<AuditSimulator />} />
        </Route>
        
        {/* Workflows */}
        <Route path="/workflows" element={<WorkflowList />} />
        <Route path="/workflows/:id" element={<WorkflowDetail />} />
        <Route path="/my/workflows" element={<MyWorkflows />} />
        
        {/* Complaints */}
        <Route path="/complaints" element={<ComplaintList />} />
        <Route path="/complaints/new" element={<FileComplaint />} />
        <Route path="/complaints/:id" element={<ComplaintDetail />} />
        
        {/* Admin Routes */}
        <Route element={<RoleRoute allowedRoles={['admin']} />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/companies" element={<AdminCompanies />} />
          <Route path="/admin/modules" element={<AdminModules />} />
          <Route path="/admin/scenarios" element={<AdminScenarios />} />
          <Route path="/admin/questions" element={<AdminQuestions />} />
          <Route path="/admin/exam-templates" element={<AdminExamTemplates />} />
          <Route path="/admin/certification-paths" element={<AdminCertificationPaths />} />
          <Route path="/admin/certificate-templates" element={<AdminCertificateTemplates />} />
          <Route path="/admin/officers" element={<AdminOfficers />} />
          <Route path="/admin/complaints" element={<AdminComplaints />} />
          <Route path="/admin/workflows" element={<AdminWorkflows />} />
          <Route path="/admin/compliance-events" element={<AdminComplianceEvents />} />
          <Route path="/admin/checklist-templates" element={<AdminChecklistTemplates />} />
          <Route path="/admin/ai-providers" element={<AdminAIProviders />} />
          <Route path="/admin/ai-prompts" element={<AdminAIPrompts />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/demo" element={<AdminDemo />} />
        </Route>
        
        {/* Profile & Settings */}
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      
      {/* 404 */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}

function App() {
  const { language, isRTL } = useLanguage()
  
  return (
    <div className={`min-h-screen ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'} lang={language}>
      <Suspense fallback={<LoadingFallback />}>
        <AppRoutes />
      </Suspense>
      <DemoBanner />
      <DemoWatermark />
      <Toaster />
    </div>
  )
}

export default App